/**
 * ConnectionsSection — Settings → Connections
 *
 * What JAI is plugged into, and how to unplug it.
 *
 * Connecting a fantasy platform hands JAI a credential — ESPN session cookies,
 * or a Yahoo OAuth token — and until now there was no way to take that back
 * short of deleting every league. Disconnecting forgets the credential and
 * leaves the leagues alone; they simply stop syncing until reconnected.
 *
 * Sleeper is shown but has no disconnect, and the copy says why: JAI reads
 * Sleeper through its public API and never holds a login for it. Offering a
 * button that claimed to revoke access we never had would be a lie.
 */

import { useEffect, useState } from 'react'
import { leaguesApi } from '../../services/api'
import { JAI_COLORS, JAI_FONTS } from '../JaiBrand'
import { GhostButton, SolidButton } from '../ModalPortal'
import { SectionNote, Notice, FieldError } from './SettingsFields'

const PLATFORMS = {
  espn: {
    name: 'ESPN',
    credential: 'session cookies',
    blurb: 'Connected through the JAI browser extension, which passes JAI your ESPN session so it can read your leagues.',
  },
  yahoo: {
    name: 'Yahoo',
    credential: 'an access token',
    blurb: 'Connected through Yahoo\'s own sign-in. JAI holds a token that lets it read your fantasy leagues.',
  },
  sleeper: {
    name: 'Sleeper',
    credential: null,
    blurb: 'Sleeper\'s league data is public, so JAI reads it without any login. There\'s no password or token stored.',
  },
}

function StatusDot({ on }) {
  return (
    <span style={{
      width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
      background: on ? JAI_COLORS.success : JAI_COLORS.mute,
    }} />
  )
}

function PlatformCard({ platform, connected, leagueCount, onDisconnect, busy }) {
  const meta = PLATFORMS[platform]
  const [confirming, setConfirming] = useState(false)

  return (
    <div style={{ padding: '15px 0', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <StatusDot on={connected} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 650, color: JAI_COLORS.navy, fontFamily: JAI_FONTS.tight }}>
            {meta.name}
          </div>
          <div style={{ marginTop: 2, fontSize: 12.5, color: JAI_COLORS.mute }}>
            {connected
              ? `Connected · ${leagueCount} league${leagueCount === 1 ? '' : 's'}`
              : leagueCount > 0
                ? `Not connected · ${leagueCount} league${leagueCount === 1 ? '' : 's'} won't sync`
                : 'Not connected'}
          </div>
        </div>

        {connected && meta.credential && !confirming && (
          <GhostButton onClick={() => setConfirming(true)}>Disconnect</GhostButton>
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.55, color: JAI_COLORS.mute }}>
        {meta.blurb}
      </div>

      {confirming && (
        <Notice tone="warn" title={`Disconnect ${meta.name}?`}>
          JAI will forget {meta.credential} and stop syncing your {meta.name} leagues.
          {' '}<strong>Your leagues and their data stay exactly as they are</strong> — they
          just won't update until you reconnect.
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
            <SolidButton tone="danger" onClick={async () => { await onDisconnect(platform); setConfirming(false) }} loading={busy}>
              {busy ? 'Disconnecting…' : `Disconnect ${meta.name}`}
            </SolidButton>
            <GhostButton onClick={() => setConfirming(false)} disabled={busy}>Keep connected</GhostButton>
          </div>
        </Notice>
      )}
    </div>
  )
}

export default function ConnectionsSection() {
  const [connections, setConnections] = useState([])
  const [leagues, setLeagues] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busyPlatform, setBusyPlatform] = useState('')
  const [flash, setFlash] = useState('')

  const load = async () => {
    try {
      const [conn, lg] = await Promise.all([
        leaguesApi.getConnections(),
        leaguesApi.getAll({ includeInactive: true }),
      ])
      setConnections(conn.data.connections || [])
      setLeagues(lg.data.leagues || [])
      setError('')
    } catch {
      setError('Could not load your connections.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const disconnect = async (platform) => {
    setBusyPlatform(platform)
    setError('')
    try {
      const { data } = await leaguesApi.disconnectPlatform(platform)
      setFlash(data.message)
      setTimeout(() => setFlash(''), 6000)
      await load()
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not disconnect. Please try again.')
    } finally {
      setBusyPlatform('')
    }
  }

  if (loading) {
    return <SectionNote>Loading your connections…</SectionNote>
  }

  return (
    <div>
      <FieldError>{error}</FieldError>
      {flash && <Notice tone="good">{flash}</Notice>}

      {connections.map(c => (
        <PlatformCard
          key={c.platform}
          platform={c.platform}
          connected={c.connected}
          leagueCount={c.leagueCount}
          busy={busyPlatform === c.platform}
          onDisconnect={disconnect}
        />
      ))}

      {/* ── Connected leagues ────────────────────────────────────────────── */}
      <div style={{ marginTop: 26, paddingTop: 20, borderTop: `1px solid ${JAI_COLORS.hair}` }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: JAI_COLORS.navy }}>
          Your leagues
        </div>

        {leagues.length === 0 ? (
          <SectionNote>
            You haven't connected a league yet. Add one from the sidebar to get
            JAI working on your team.
          </SectionNote>
        ) : (
          <div style={{ marginTop: 10 }}>
            {leagues.map(l => {
              const inactive = l.active === false
              return (
                <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: `1px solid ${JAI_COLORS.hair}` }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: JAI_COLORS.navy }}>
                      {l.name}
                      {inactive && (
                        <span style={{ marginLeft: 8, fontSize: 11.5, fontWeight: 600, color: JAI_COLORS.mute }}>
                          Paused
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: 2, fontSize: 12, color: JAI_COLORS.mute }}>
                      {[String(l.sport || '').toUpperCase(), PLATFORMS[l.platform]?.name || l.platform, l.season]
                        .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              )
            })}
            <SectionNote>
              Leagues are added and removed from the sidebar. "Paused" leagues
              don't count against your plan's league limit.
            </SectionNote>
          </div>
        )}
      </div>
    </div>
  )
}
